/**
 * Offline Storage Service
 *
 * Manages persistent offline queue in localStorage.
 * Handles queue CRUD operations, retry scheduling, and quota management.
 *
 * Features:
 * - Schema versioning for future migrations
 * - Automatic quota overflow handling
 * - Exponential backoff calculation
 * - Type-safe queue operations
 */

import { v4 as uuidv4 } from 'uuid';
import type {
  OfflineQueue,
  PendingSubmission,
} from '../types/offline';

// LocalStorage key for offline queue
const STORAGE_KEY = 'patient_survey_offline_queue';

// Current schema version (increment when structure changes)
const SCHEMA_VERSION = 1;

// Maximum queue size to prevent localStorage quota overflow
const MAX_QUEUE_SIZE = 100;

/**
 * OfflineStorageService
 *
 * Provides static methods for managing offline submission queue.
 * All operations are synchronous using localStorage.
 */
export class OfflineStorageService {
  /**
   * Load offline queue from localStorage
   *
   * Handles schema version migrations and error recovery.
   * Returns empty queue if none exists or parsing fails.
   *
   * @returns Current offline queue
   */
  static getQueue(): OfflineQueue {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);

      if (!stored) {
        return {
          version: SCHEMA_VERSION,
          submissions: [],
        };
      }

      const queue: OfflineQueue = JSON.parse(stored);

      // Version migration (future-proofing)
      if (queue.version !== SCHEMA_VERSION) {
        console.warn(
          `Offline queue version mismatch: ${queue.version} → ${SCHEMA_VERSION}. Resetting queue.`
        );
        return {
          version: SCHEMA_VERSION,
          submissions: [],
        };
      }

      return queue;
    } catch (error) {
      console.error('Failed to load offline queue:', error);
      return {
        version: SCHEMA_VERSION,
        submissions: [],
      };
    }
  }

  /**
   * Save offline queue to localStorage
   *
   * Enforces max queue size and handles quota overflow.
   * If quota exceeded, removes oldest submission and retries.
   *
   * @param queue - Queue to save
   */
  static saveQueue(queue: OfflineQueue): void {
    try {
      // Enforce max size (remove oldest if exceeding)
      if (queue.submissions.length > MAX_QUEUE_SIZE) {
        console.warn(
          `Queue size (${queue.submissions.length}) exceeds max (${MAX_QUEUE_SIZE}). Removing oldest submissions.`
        );
        queue.submissions = queue.submissions.slice(-MAX_QUEUE_SIZE);
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
    } catch (error) {
      // Handle quota exceeded error
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.warn(
          'LocalStorage quota exceeded. Removing oldest submission and retrying.'
        );

        // Remove oldest submission and retry
        if (queue.submissions.length > 0) {
          queue.submissions = queue.submissions.slice(1);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
        }
      } else {
        console.error('Failed to save offline queue:', error);
      }
    }
  }

  /**
   * Add a new submission to the queue
   *
   * Generates unique ID and creation timestamp.
   * Saves queue immediately after addition.
   *
   * @param submission - Submission data (without id and createdAt)
   * @returns Generated submission ID
   */
  static addSubmission(
    submission: Omit<PendingSubmission, 'id' | 'createdAt'>
  ): string {
    const queue = this.getQueue();

    const newSubmission: PendingSubmission = {
      ...submission,
      id: uuidv4(),
      createdAt: new Date().toISOString(),
    };

    queue.submissions.push(newSubmission);
    this.saveQueue(queue);

    return newSubmission.id;
  }

  /**
   * Update an existing submission in the queue
   *
   * Sets lastAttemptAt timestamp automatically.
   * No-op if submission ID not found.
   *
   * @param id - Submission ID to update
   * @param updates - Partial submission data to merge
   */
  static updateSubmission(
    id: string,
    updates: Partial<PendingSubmission>
  ): void {
    const queue = this.getQueue();
    const index = queue.submissions.findIndex((s) => s.id === id);

    if (index !== -1) {
      queue.submissions[index] = {
        ...queue.submissions[index],
        ...updates,
        lastAttemptAt: new Date().toISOString(),
      };
      this.saveQueue(queue);
    }
  }

  /**
   * Remove a submission from the queue
   *
   * Updates lastSyncAt timestamp on successful removal.
   * Used when submission completes successfully.
   *
   * @param id - Submission ID to remove
   */
  static removeSubmission(id: string): void {
    const queue = this.getQueue();
    queue.submissions = queue.submissions.filter((s) => s.id !== id);
    queue.lastSyncAt = new Date().toISOString();
    this.saveQueue(queue);
  }

  /**
   * Get submissions that are ready for retry
   *
   * Filters submissions where:
   * - Status is not 'exhausted'
   * - Next retry time has arrived
   *
   * @returns Array of submissions ready for retry
   */
  static getRetryableSubmissions(): PendingSubmission[] {
    const queue = this.getQueue();
    const now = new Date();

    return queue.submissions.filter((submission) => {
      // Exclude exhausted submissions
      if (submission.status === 'exhausted') {
        return false;
      }

      // Include if retry time has arrived
      const nextRetry = new Date(submission.nextRetryAt);
      return nextRetry <= now;
    });
  }

  /**
   * Get count of pending submissions
   *
   * Includes all submissions except exhausted ones.
   *
   * @returns Number of pending submissions
   */
  static getPendingCount(): number {
    const queue = this.getQueue();
    return queue.submissions.filter((s) => s.status !== 'exhausted').length;
  }

  /**
   * Clear entire offline queue
   *
   * ⚠️ Destructive operation - removes all pending submissions.
   * Use with caution, typically only for testing or reset.
   */
  static clearQueue(): void {
    localStorage.removeItem(STORAGE_KEY);
  }

  /**
   * Calculate next retry time with exponential backoff
   *
   * Backoff schedule:
   * - Attempt 0: 1 second
   * - Attempt 1: 2 seconds
   * - Attempt 2: 4 seconds
   * - Attempt 3: 8 seconds
   * - Attempt 4: 16 seconds
   *
   * @param retryCount - Number of retries attempted so far
   * @returns ISO 8601 timestamp of next retry
   */
  static calculateNextRetry(retryCount: number): string {
    const baseDelayMs = 1000; // 1 second
    const delayMs = baseDelayMs * Math.pow(2, retryCount);
    const nextRetryDate = new Date(Date.now() + delayMs);
    return nextRetryDate.toISOString();
  }
}
